<template>
  <div class="input--wrap"
       :class="[`label-${labelPosition}`, {'above-input': isInputAbove}, inputType, size, {dark: darkMode}]">
    <label :for="id" class="label"><slot /></label>
    <div class="input-element"
         :class="{ error: modelError || error }">
      <textarea v-if="inputType === 'textarea'"
                ref="input"
                :autocomplete="autocomplete"
                :disabled="disabled"
                :type="inputType"
                :id="id"
                :placeholder="placeholder"
                :name="name"
                :key="updateKey"
                :readonly="readonly"
                v-model="model"
                :rows="rows"
                @keyup="onKeyup"
                @blur="onBlur"
                @focus="onFocus" />
      <input v-else
             ref="input"
             :autocomplete="autocomplete"
             :disabled="disabled"
             :type="inputType"
             :id="id"
             :placeholder="placeholder"
             :name="name"
             :key="updateKey"
             :readonly="readonly"
             v-model="model"
             @keyup="onKeyup"
             @blur="onBlur"
             @focus="onFocus"
             @keyup.enter="onEnter" />
        <i v-if="!disabled && !readonly && model.length && type !== 'password' && (!modelError || errorType !== 'iconOnly')"
         class="icon icon--close14"
         :class="{ white: white }"
         @mousedown.prevent="resetValue"></i>
      <i v-if="modelError.length && errorType === 'iconOnly'"
         class="icon icon--triangle-alert"></i>
      <div v-if="errorTooltip && errorType === 'iconOnly'" class="input-error-pop" >
        <p v-html="errorTooltip"></p>
      </div>
      <i v-if="type === 'password'"
         class="icon icon--pw-eye"
         :class="{on: !toggleInputType}"
         @click="toggleInputType = !toggleInputType"></i>
      <transition name="fade">
        <strong v-if="modelError.length && errorType === ''" class="validation-text" v-html="$t(`common.validationErrorText.${modelError}`)"></strong>
        <strong v-if="error.length" class="validation-text" v-html="error"></strong>
        <strong v-else-if="modelDescription.length" class="validation-text description" v-html="modelDescription"></strong>
      </transition>
    </div>
  </div>
</template>

<script>
import { substrByBytes } from '@/libs/utils'

export default {
  props: {
    isInputAbove: {
      type: Boolean,
      default: true,
      required: false
    },
    autoFocus: {
      type: Boolean,
      default: false,
      required: false
    },
    autocomplete: {
      type: String,
      default: 'on',
      required: false
    },
    labelPosition: {
      type: String, // top, left
      default: 'top',
      required: false
    },
    type: {
      type: String,
      default: 'text',
      required: true
    },
    id: {
      type: String,
      required: false
    },
    placeholder: {
      type: String,
      default: 'Please enter text',
      required: false
    },
    name: {
      type: String,
      default: 'inputValue',
      required: false
    },
    modelValue: {
      default: '',
      required: false
    },
    min: {
      required: false
    },
    max: {
      required: false
    },
    byteLimit: {
      type: Number,
      required: false
    },
    modelError: {
      type: String,
      default: '',
      required: false
    },
    errorTooltip: {
      type: String,
      default: '',
      required: false
    },
    error: {
      type: String,
      default: '',
      required: false
    },
    modelDescription: {
      type: String,
      default: '',
      required: false
    },
    disabled: {
      type: Boolean,
      default: false,
      required: false
    },
    white: {
      type: Boolean,
      default: false,
      required: false
    },
    readonly: {
      type: Boolean,
      default: false,
      required: false
    },
    reset: {
      type: Boolean,
      default: false,
      required: false
    },
    lower: {
      type: Boolean,
      default: false,
      required: false
    },
    errorType: {
      type: String,
      default: '',
      required: false
    },
    size: {
      type: String,
      default: '' // small
    },
    darkMode: {
      type: Boolean,
      default: false
    },
    rows: {
      type: Number,
      default: 2
    }
  },
  watch: {
    model (value) {
      const newValue = this.lower ? value.toLowerCase() : value
      this.model = newValue
    },
    modelValue (value) {
      this.model = value
    },
    focused (value) {
      if (!value) {
        this.updateKey++
      }
    },
    reset (value) {
      if (value) {
        this.model = ''
      }
    }
  },
  created () {
    if (this.modelValue) {
      this.model = this.modelValue
    }
  },
  mounted () {
    if (this.autoFocus) {
      setTimeout(() => {
        this.$refs.input.focus()
      }, 100)
    }
  },
  data () {
    return {
      model: '',
      focused: false,
      toggleInputType: true,
      updateKey: 0
    }
  },
  computed: {
    inputType () {
      if (this.type === 'password') {
        return this.pwShowToggle ? 'password' : 'text'
      } else if (this.type === 'number') {
        return 'text'
      } else {
        return this.type
      }
    },
    pwShowToggle () {
      if (this.type === 'password') {
        return this.toggleInputType
      } else {
        return false
      }
    }
  },
  methods: {
    resetValue () {
      this.model = ''
      this.$emit('input', this.model)
      this.$emit('onKeyup')
    },
    constraintValue () {
      let update = false
      if (this.type === 'number') {
        const convert = parseInt(String(this.model).replace(/[^0-9.-]/g, ''))
        if (!convert || isNaN(convert)) {
          update = 0
        } else {
          update = convert
        }
        if (this.min && this.min > update) {
          update = this.min
        }
        if (this.max && this.max < update) {
          update = this.max
        }
        update = String(update)
      }
      if (update) {
        this.model = update
      }
    },
    constraintByte () {
      let update = false
      if (this.byteLimit) {
        update = substrByBytes(String(this.model), this.byteLimit)
      }
      if (update) {
        this.model = update
      }
    },
    onKeyup (event) {
      this.constraintByte()
      this.$emit('input', this.model, event)
    },
    onBlur () {
      this.constraintValue()
      this.focused = false
      this.$emit('input', this.model)
      this.$emit('onBlur')
    },
    onFocus () {
      this.focused = true
      this.$emit('onFocus')
    },
    onEnter () {
      this.$emit('onEnter')
    }
  }
}
</script>

<style lang="scss" scoped="scoped">
.input--wrap {
  width: 100%;
  position: relative;
  display: inline-block;
  text-align: left;
  margin: 0 auto;
  &.label-hidden {
    .label {
      margin: 0;
    }
  }
  &.label-left {
    @include flexYcenter;
    justify-content: center;
    .label {
      display: inline-block;
      padding-left: 0;
      margin-right: 10px;
      margin-bottom: 0;
    }
    textarea,
    input {
      display: inline-block;
    }

    &.above-input {
      margin-top: 40px;
    }
  }

  &.above-input {
    margin-top: 22px;
  }

  .label {
    display: inline-block;
    font-weight: 500;
    padding-left: 5px;
    margin-bottom: 5px;
    font-size: 13px;
    color: #767676;
  }

  .input-element {
    position: relative;
    width: 100%;

    &.error {
      textarea,
      input {
        border-color: $errorColor !important;
      }
      .validation-text {
        color: $errorColor;

        .description {
          color: #289eef;
        }
      }
    }

    input,
    textarea {
      display: block;
      width: 100%;
      height: 38px;
      line-height: 36px;
      border-radius: 6px;
      border: solid 1px #bbbbbb;
      background-color: #ffffff;
      font-size: 14px;
      padding-left: 12px;
      padding-right: 40px;
      box-sizing: border-box;
      transition: opacity 0.2s ease;
      transform: translateZ(0);
      color: #2f2f2f;

      &::-ms-clear {
        display: none;
      }
      &::-ms-reveal {
        display: none;
      }

      &:focus:not([readonly]) {
        border-color: #289eef;
      }

      &[readonly] {
        cursor: default;
      }

      &[disabled] {
        background-color: #d7d7d7;
        opacity: 0.3;

        &::placeholder {
          color: #999999;
        }
      }
    }

    textarea {
      height: 150px;
      resize: none;
      line-height: 22px;
      padding-top: 7px;
      padding-bottom: 7px;
      color: #444;
    }
  }

  .icon--check-green-size16 {
    display: none;
  }

  .icon--close14,
  .icon--check-green-size16,
  .icon--triangle-alert {
    position: absolute;
    top: 50%;
    right: 0;
    padding: 12px;
    transform: translateY(-50%);
    cursor: pointer;
    background-position: center;
    background-color: transparent;
  }

  .icon--triangle-alert {
    padding: 0;
    right: 9px;
  }

  &.textarea {
    .icon--close14,
    .icon--check-green-size16 {
      top: 4px;
      right: 5px;
      transform: none;
    }
  }

  .icon--pw-eye {
    position: absolute;
    top: 0;
    right: 2px;
    padding: 6px 0 6px 12px;
    cursor: pointer;
    background-position: center;
    background-color: transparent;
  }

  .validation-text {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    padding-left: 5px;
    font-weight: 500;
    line-height: 1.2;

    &.description {
      color: #289eef;
    }

    &.fade-enter-active,
    &.fade-leave-active {
      opacity: 1;
      transition: opacity 0.15s ease;
    }
    &.fade-enter,
    &.fade-leave-to{
      opacity: 0;
    }
  }
  .input-error-pop {
    position: absolute;
    bottom: 100%;
    right: -4px;
    color: #E60724;
    background-color: #fff;
    padding: 9px 10px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.5;

    text-align: center;
    text-shadow: none;
    z-index: 2147483647;
    word-break: keep-all;
    word-wrap: break-word;
    box-sizing: border-box;
    box-shadow:
      0 8px 15px 0 rgba(0, 0, 0, 0.16),
      0 0 0 1px rgba(0, 0, 0, 0.16);
    &:after {
      content: '';
      position: absolute;
      background: inherit;
      width: 10px;
      height: 10px;
      top: 100%;
      right: 10px;
      transform: translate(-50%, -50%) rotate(-45deg);
      box-shadow: -1px 1px 0 0 rgba(0, 0, 0, 0.16);
    }
  }
  &.small {
    input, textarea {
      height: 32px;
      padding: 6px 10px;
      border-radius: 5px;
    }
    .icon--close14,
    .icon--check-green-size16,
    .icon--triangle-alert {
      padding: 6px;
    }
  }
  &.dark {
    label {
      color: #C7C7C7;
    }
    input, textarea {
      background-color: #555;
      color: #fff;
      border-color: #B1B1B1;
      &::placeholder {
        color: #aaa;
      }
    }
  }
}
</style>
